import { useState, useEffect, useRef, useCallback } from 'react';
import { listOrgAgents, stopOrgAgent, stopMyOrgAgents } from '../api/orgs';
import { useConfirm } from './ConfirmDialog';
import {
  agentKindLabel,
  agentStateClass,
  countStoppableMine,
  formatTokenPair,
  formatTokens,
  sortAgents,
} from './agentMonitorRows';

function fmtDuration(ms) {
  if (!isFinite(ms) || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function fmtStarted(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * The Agents tab of the process monitor — org-wide LLM/agent activity.
 *
 * Sibling of the Processes tab, and deliberately built to the same shape: two
 * meters, a meta row, one table of rows. What differs is the resource, because an
 * agent consumes no worker-job seat and so appears nowhere in the CPU/GPU
 * accounting: what it consumes is provider tokens.
 *
 * Takes org + identity as PROPS rather than self-fetching (unlike
 * `StorageUsageView`, which needs neither): `ProcessMonitorButton` has already
 * resolved both on open, and a second `getCurrentOrg`/`getUser` round trip per tab
 * switch would be pure waste. It owns its own poll so the Processes tab's poll can
 * stop while this tab is showing.
 */
export default function AgentMonitorView({ orgId, currentUserId, canKillAny }) {
  const confirm = useConfirm();
  const [agents, setAgents] = useState([]);
  const [meta, setMeta] = useState({
    active: { running: 0, limit: 0, queuedTurns: 0, parked: 0 },
    llm: { inFlight: 0, limit: 0 },
    tokens: { day: null, in: 0, out: 0, calls: 0 },
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible');
  const pollRef = useRef(null);

  const refresh = useCallback(async (id) => {
    if (!id) return;
    try {
      const data = await listOrgAgents(id);
      setAgents(data.agents || []);
      setMeta({
        active: data.active || { running: 0, limit: 0, queuedTurns: 0, parked: 0 },
        llm: data.llm || { inFlight: 0, limit: 0 },
        tokens: data.tokens || { day: null, in: 0, out: 0, calls: 0 },
      });
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    if (!orgId) return undefined;
    let active = true;
    setLoading(true);
    (async () => {
      await refresh(orgId);
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [orgId, refresh]);

  // Track tab visibility so a backgrounded tab can pause polling.
  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Poll every 3s while visible, and immediately on regaining visibility so a
  // returning tab isn't stale — same cadence and gating as the Processes tab.
  useEffect(() => {
    if (!orgId || !visible) return undefined;
    refresh(orgId);
    pollRef.current = setInterval(() => refresh(orgId), 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [orgId, visible, refresh]);

  // 1s ticker re-renders live durations; no network.
  useEffect(() => {
    if (!visible || agents.length === 0) return undefined;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [visible, agents.length]);

  const rows = sortAgents(agents, currentUserId);
  const myStoppable = countStoppableMine(agents, currentUserId);

  async function handleStop(agent) {
    const who =
      agent.ownerUserId === currentUserId
        ? ''
        : ` (owned by ${agent.displayName || agent.ownerUserId})`;
    if (
      !(await confirm({
        title: 'Stop assistant?',
        body: `Stop ${agentKindLabel(agent)}${who}? Its run ends and any module it was driving is left as-is.`,
        danger: true,
        confirmText: 'Stop',
      }))
    )
      return;
    try {
      await stopOrgAgent(orgId, agent.agentId);
      setAgents((prev) => prev.filter((a) => a.agentId !== agent.agentId)); // poll reconciles
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleStopAllMine() {
    if (!myStoppable) return;
    if (
      !(await confirm({
        title: 'Stop all your assistants?',
        body: `Stop all ${myStoppable} of your assistant runs? Any modules they were driving are left as-is.`,
        danger: true,
        confirmText: 'Stop all',
      }))
    )
      return;
    try {
      await stopMyOrgAgents(orgId);
      setAgents((prev) => prev.filter((a) => !(a.stoppable && a.ownerUserId === currentUserId)));
    } catch (err) {
      setError(err.message);
    }
  }

  if (!orgId) {
    return <p className="procmon-empty">You're not part of an organization yet.</p>;
  }

  return (
    <>
      <div className="procmon-capacity">
        <div className="procmon-meters">
          <div className="procmon-meter">
            <span className="procmon-meter-count">
              {meta.active.running} / {meta.active.limit || '—'}
            </span>
            <span className="procmon-meter-label">
              Assistants running
              {meta.active.queuedTurns ? ` · ${meta.active.queuedTurns} queued` : ''}
              {meta.active.parked ? ` · ${meta.active.parked} need input` : ''}
            </span>
          </div>
          <div className="procmon-meter">
            <span className="procmon-meter-count">
              {meta.llm.inFlight} / {meta.llm.limit || '—'}
            </span>
            <span className="procmon-meter-label">Model calls in flight</span>
          </div>
          <div className="procmon-meter">
            <span className="procmon-meter-count">
              {formatTokenPair(meta.tokens.in, meta.tokens.out)}
            </span>
            <span className="procmon-meter-label">
              Tokens today (in / out)
              {meta.tokens.calls ? ` · ${formatTokens(meta.tokens.calls)} calls` : ''}
            </span>
          </div>
        </div>
        <div className="procmon-meta">
          <span
            className="procmon-compute"
            title="Assistants run on an LLM provider, not on AWS — they consume no CPU or GPU worker slot, which is why they appear on their own tab."
          >
            No CPU/GPU slot · billed in tokens
          </span>
          {myStoppable > 0 && (
            <button
              className="procmon-stop procmon-stop-all"
              onClick={handleStopAllMine}
              title="Stop every assistant run you own"
            >
              Stop all mine ({myStoppable})
            </button>
          )}
        </div>
      </div>

      {error && <div className="procmon-error">{error}</div>}

      {rows.length === 0 ? (
        <p className="procmon-empty">
          {loading ? 'Loading…' : 'No assistants are running right now.'}
        </p>
      ) : (
        <table className="procmon-table">
          <thead>
            <tr>
              <th>Assistant</th>
              <th>Owner</th>
              <th>Started</th>
              <th>Duration</th>
              <th>Model</th>
              <th>Tokens</th>
              <th>State</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const mine = a.ownerUserId === currentUserId;
              const ms = Date.parse(a.startedAt || '');
              const dur = isNaN(ms) ? NaN : nowTick - ms;
              const canStop = a.stoppable && (mine || canKillAny);
              return (
                <tr
                  key={a.agentId}
                  className={`${mine ? 'procmon-row-mine' : ''}${
                    a.kind === 'subagent' ? ' procmon-row-child' : ''
                  }`}
                >
                  <td>
                    <div className="procmon-name">{agentKindLabel(a)}</div>
                    <div
                      className="procmon-source"
                      title={
                        a.projectId
                          ? `${a.projectId}${a.tileId ? ` · ${a.tileId}` : ''}`
                          : undefined
                      }
                    >
                      {(a.projectName ||
                        (a.projectId ? `${String(a.projectId).slice(0, 8)}…` : '—')) +
                        (a.nodeLabel || a.tileType ? ` · ${a.nodeLabel || a.tileType}` : '')}
                    </div>
                  </td>
                  <td>
                    {a.displayName || a.ownerUserId || '—'}
                    {mine && <span className="procmon-you"> (you)</span>}
                  </td>
                  <td>{fmtStarted(a.startedAt)}</td>
                  <td>{fmtDuration(dur)}</td>
                  <td className="procmon-agent-model">{a.model || '—'}</td>
                  <td title={`${a.tokensIn || 0} in / ${a.tokensOut || 0} out`}>
                    {formatTokenPair(a.tokensIn, a.tokensOut)}
                    {a.rounds ? <span className="procmon-you"> · {a.rounds}r</span> : null}
                  </td>
                  <td>
                    <span className={`procmon-status procmon-agent-${agentStateClass(a.state)}`}>
                      {a.state}
                    </span>
                  </td>
                  <td>
                    {canStop ? (
                      <button className="procmon-stop" onClick={() => handleStop(a)}>
                        Stop
                      </button>
                    ) : (
                      // Deliberately not a disabled button: a browser-driven round is
                      // a streaming response on one backend instance and cannot be
                      // killed from another. It finishes on its own in seconds, so
                      // saying that is more useful than offering a dead control.
                      <span className="procmon-agent-nostop" title="This finishes on its own">
                        —
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}
